import EventType from 'ol/events/EventType.js';
import MapBrowserEvent from 'ol/MapBrowserEvent.js';
import {Options} from 'ol/interaction/Pointer.js';

import LineString from 'ol/geom/LineString';
import VectorSource from 'ol/source/Vector';
import { RouteDelegate } from '@/RouteDelegate';
import { Feature } from 'ol';
import { Pixel } from 'ol/pixel';
import CustomInteractionLifecycle from './CustomInteractionLifecycle';
import Point from 'ol/geom/Point';
import { TrackEntity } from '@/TrackEntity';
import AnchorPointFeature from './AnchorPointFeature';
import { GeometryHelpers } from '@/geometryhelpers';
import { Coordinate } from 'ol/coordinate';
import { Renderer } from './Renderer';
import * as _ from 'lodash'
import CustomInteractionLifecycleTrack from './CustomInteractionLifecycleTrack';
import PreUndoEvent from './PreUndoEvent';
import GridPointHelper from './GridPointHelper';
import UserEventInfo from './UserEventInfo';
import TrackVectorLayer from './TrackVectorLayer';
import { TrackVectorLayerType } from './TrackVectorLayerType';
import JoinHelper, { JoinInfo } from './JoinHelper';
import DeleteTrackData from './DeleteTrackData';
import { RerouteErrorEvent } from './reroute';
import EventRememberingPointerInteraction from './EventRememberingPointerInteraction';

export interface DrawRouteOptions {
    track: TrackEntity;
    routeDelegate: RouteDelegate;
    routingLineFeatureSource: VectorSource<LineString>; // only for the movingLine
    joinIndicatorFeatureSource: VectorSource<Point>;
    renderer: Renderer<any>;
}

export enum DrawRouteEvents {
    routeerror = 'routeerror',
    finisheddrawing = 'finisheddrawing'
}

interface HoldDownInfo {
    func: _.DebouncedFunc<() => void>;
    pixel: Pixel;
}

export default class DrawRoute extends EventRememberingPointerInteraction implements CustomInteractionLifecycle, CustomInteractionLifecycleTrack {
    options: DrawRouteOptions
    movingLineStart: Coordinate|undefined
    implementsCustomInteractionLifecycle = true
    implementsCustomInteractionLifecycleTrack = true
    startCoordinate: Coordinate|null = null
    holdDownInfo: HoldDownInfo|null = null
    holdDownEvent: Event|null = null
    joinHelper: JoinHelper
    lastMouseEvent: MapBrowserEvent<any>|undefined
    holdDownOriginalEventTimestamp = null as number|null

    constructor(options: DrawRouteOptions, pointerOptions?: Options) {
        super(pointerOptions)
        this.joinHelper = new JoinHelper(options.joinIndicatorFeatureSource, this, options.renderer)
        this.options = options
        this.continueIfExisting()
    }

    trackChanged() {
        this.continueIfExisting() // e.g. on undo
    }

    continueIfExisting() {
        // does the track already exist? if so, extend it
        const routeFeatures = this.options.track.getSegments()
        if(routeFeatures.length > 0) {
            this.movingLineStart = routeFeatures[routeFeatures.length - 1].getGeometry().getLastCoordinate()
            this.setMovingLine(new LineString([this.movingLineStart, this.lastMouseEvent?.coordinate || this.movingLineStart]))
        } else {
            // not drawn any actual lines, but clicked the start point
            const dotsFeatures = this.options.track.getDots()
            if(dotsFeatures.length > 0) {
                this.movingLineStart = dotsFeatures[dotsFeatures.length - 1].getGeometry().getLastCoordinate()
                this.setMovingLine(new LineString([this.movingLineStart, this.lastMouseEvent?.coordinate || this.movingLineStart]))
            } else {
                this.removeMovingLine()
            }
        }
    }

    setMovingLine(lineString: LineString) {
        // we want to end up with only one feature in routingLineFeatureSource, movingLine.
        // any other previous 'orphaned' instances of movingLine, we don't want those in routingLineFeatureSource.

        // TODO: can we refactor this to be a lot simpler by just clearing it and adding a fresh one, regardless of whether it already exists?
        const routingLines = this.options.routingLineFeatureSource.getFeatures()
        let routingLine = _.first(routingLines)
        if(routingLine) {
            routingLine.setGeometry(lineString)
        } else {
            routingLine = new Feature<LineString>(lineString)
            this.options.routingLineFeatureSource.addFeature(routingLine)
        }
    }

    finish() {
        if(this.options.track.getSegments().length === 0) {
            // remove the start dot if we haven't actually drawn anywhwere
            this.options.track.clearDots()
        }
        this.removeMovingLine()
    }

    removeMovingLine() {
        this.options.routingLineFeatureSource.clear()
   }

    handleEvent(event: MapBrowserEvent<any>) {
        this.lastMouseEvent = event
        if (event.originalEvent.type === EventType.CONTEXTMENU) {
            // Avoid context menu for long taps when drawing on mobile
            event.preventDefault();
        }

        if(event.type === 'pointerup' || event.type === 'pointerdown') {
            if(this.holdDownInfo !== null) {
                this.holdDownInfo.func.cancel()
                this.holdDownInfo = null
            }
        }

        if(this.holdDownInfo !== null && event.type === 'pointermove' && !GeometryHelpers.isVeryClose(this.holdDownInfo.pixel, event.pixel)) {
            this.holdDownInfo.func.cancel()
            this.holdDownInfo = null
        }

        if(event.type === 'singleclick') {
            if(event.originalEvent !== this.holdDownEvent) {
                return this.handleSingleClick(event)
            }
        } else if(event.type === 'pointerdown') {
            this.holdDownOriginalEventTimestamp = null
            this.holdDownInfo = {
                func: _.debounce(() => this.heldDown(event), 1000),
                pixel: event.pixel
            }
            this.holdDownInfo.func()
        }

        return super.handleEvent(event)
    }

    heldDown(event: MapBrowserEvent<any>) {

        this.holdDownOriginalEventTimestamp = event.originalEvent.timeStamp
        this.holdDownEvent = event.originalEvent
        this.drawStraightLine(event)
    }

    drawStraightLine(event: MapBrowserEvent<any>)  {
        const lastDot = _.last(this.options.track.getDots())
        if(lastDot === undefined) {
            // not started yet - same as when routing
            this.handleSingleClick(event)
        } else {
            const joinInfo = this.joinHelper.getJoinInfo(event)
            const startPoint = lastDot.getGeometry();
            const startCoord = startPoint.getFirstCoordinate()
            const endCoord = joinInfo.coord
            const trackLine = new LineString([startCoord, endCoord])
            const routeLine = new LineString([startCoord, endCoord])
            this.routeSuccess(routeLine, trackLine, joinInfo, event)
        }
    }

    routeSuccess(request: LineString, response: LineString, joinInfo: JoinInfo, originalEvent: MapBrowserEvent<any>) {
        this.dispatchEvent(new PreUndoEvent([this.options.track.getRestoreData(this.options.renderer)], 'draw'))

        const startEvent = undefined // we are saying the start is fixed, purely because we 'snap' it on creation by using the 'nearest' API
        // (user gets instant visual feedback of it moving onto a road)
        const intendsAtcf = false

        const snappedLineInfo = this.options.routeDelegate.getSnappedRouteInfo(request, response, startEvent, joinInfo.event, intendsAtcf)

        const newAnchorPointToCreate = new Point(snappedLineInfo.endPointToUse);
        this.options.track.addSegment(snappedLineInfo.trackLineToUse)
        this.options.track.addDot(newAnchorPointToCreate)

        if(joinInfo.trackToJoin) {
            const joinedTrack = this.joinHelper.joinTracks(this.options.track, joinInfo.trackToJoin)
            this.movingLineStart = _.last(joinedTrack.getDots())!.getGeometry().getCoordinates()
        } else {
            this.movingLineStart = snappedLineInfo.endPointToUse
        }
        this.setMovingLine(new LineString([this.movingLineStart, originalEvent.coordinate]))
    }

    handleSingleClick(event: MapBrowserEvent<any>) {
        // if it's in response to a hold-down, we don't want to do anything, as the holdDown func will already have done it.
        // we don't want to simply wait for handleSingleClick and check the time difference between down and up,
        // because we want the event to fire as soon as the timer is up, without the user having to mouse up
        if (this.holdDownOriginalEventTimestamp === null || this.holdDownOriginalEventTimestamp !== event.originalEvent.timeStamp) {
            const lastDot = _.last(this.options.track.getDots())
            if(lastDot === undefined) {
                // not started yet. let's start.
                this.options.routeDelegate.snapCoord(event.coordinate, snappedCoord => {
                    this.dispatchEvent(new PreUndoEvent([new DeleteTrackData(this.options.track.value, false)], 'start track'))

                    // is the snapped point very close, in pixels, to the snapped one?
                    const startPointInfo = GridPointHelper.querySnap(this.getMap(), event, snappedCoord)
                    this.options.track.addDot(new Point(startPointInfo.coords))
                    this.setMovingLine(new LineString([startPointInfo.coords, event.coordinate]))
                    this.startCoordinate = startPointInfo.coords
                    this.movingLineStart = startPointInfo.coords
                }, (errorMessage) => {
                    this.dispatchEvent(new RerouteErrorEvent(errorMessage))
                })
            } else {
                // continuing the route
                const startPoint = lastDot.getGeometry();
                const startCoord = startPoint.getCoordinates()
                const startPixel = this.getMap()!.getPixelFromCoordinate(startCoord)
                const joinInfo = this.joinHelper.getJoinInfo(event)
                if(joinInfo.trackToJoin === undefined && GeometryHelpers.isVeryClose(startPixel, event.pixel)) {
                    this.removeMovingLine()
                    this.dispatchEvent(DrawRouteEvents.finisheddrawing)
                } else {
                    const routeLine = new LineString([startCoord, joinInfo.coord])
                    this.options.routeDelegate.doRoute(routeLine, trackLine => this.routeSuccess(routeLine, trackLine, joinInfo, event), (errorMessage) => this.dispatchEvent(new RerouteErrorEvent(errorMessage)))
                }
            }
            return true
        } else {
            return false
        }
    }

    handleMoveEvent(event: MapBrowserEvent<any>) {
        if(this.movingLineStart) {
            this.setMovingLine(new LineString([this.movingLineStart, event.coordinate]))
            this.checkFeatureToJoin(event)
        }
        return super.handleMoveEvent(event)
    }

    doCheckFeatureToJoin(event: MapBrowserEvent<any>) {
        const featuresNearby = event.map.getFeaturesAtPixel(event.pixel, {
            hitTolerance: 10,
            layerFilter: layerSource => layerSource instanceof TrackVectorLayer && layerSource.type === TrackVectorLayerType.Dots
        })
        const joinableTrackpointFeaturesNearby = featuresNearby
            .filter(f => f instanceof AnchorPointFeature && this.canJoin(f))
            .map(f => f as AnchorPointFeature)
        const trackpointFeatureToJoin = _.last(_.sortBy(joinableTrackpointFeaturesNearby, f => f.track.zIndex))
        if(trackpointFeatureToJoin) {
            this.joinHelper.setFeatureToJoin(trackpointFeatureToJoin)
        } else {
            this.joinHelper.unsetFeatureToJoin()
        }
    }
    checkFeatureToJoin = _.throttle(this.doCheckFeatureToJoin, 200)

    canJoin(tp: AnchorPointFeature) {
        const retval = tp.track.value !== this.options.track.value && tp.isFirst
        return retval
    }
}
